import { Alert } from "@connected-repo/ui-mui/feedback/Alert";
import { Button } from "@connected-repo/ui-mui/form/Button";
import { useSessionInfo } from "@frontend/contexts/UserContext";
import { authClient } from "@frontend/utils/auth.client";
import { useState } from "react";

const DISMISSED_KEY = "confirm-email-banner-dismissed";

const readDismissed = (): boolean => {
	try {
		return sessionStorage.getItem(DISMISSED_KEY) === "1";
	} catch {
		return false;
	}
};

/** Where the emailed confirmation link sends the browser, on this host. */
export const emailVerifiedCallbackUrl = () => `${window.location.origin}/auth/email-verified`;

/**
 * Asks a signed-in person whose email is not confirmed to confirm it, with
 * "Resend" for a new link (better-auth `sendVerificationEmail`, Novu's
 * `email-verification`). Email + password sign-ups get the first link by
 * themselves; accounts an organiser or the exhibitor import made start
 * unconfirmed and get theirs from here. Signing in never waits on it.
 * Hidden for the rest of the tab once dismissed.
 */
export const ConfirmEmailBanner = () => {
	const { user } = useSessionInfo();
	const [dismissed, setDismissed] = useState(readDismissed);
	const [state, setState] = useState<"idle" | "sending" | "sent" | "failed">("idle");

	// A phone sign-up carries a made-up address; there is nothing to confirm.
	const email = user?.email;
	if (!user || user.emailVerified || !email || email.endsWith("@temp-local.com") || dismissed) {
		return null;
	}

	const resend = async () => {
		setState("sending");
		try {
			await authClient.sendVerificationEmail(
				{ email, callbackURL: emailVerifiedCallbackUrl() },
				{ throw: true },
			);
			setState("sent");
		} catch {
			setState("failed");
		}
	};

	const dismiss = () => {
		try {
			sessionStorage.setItem(DISMISSED_KEY, "1");
		} catch {
			// The banner just comes back on the next page load.
		}
		setDismissed(true);
	};

	return (
		<Alert
			severity={state === "failed" ? "warning" : "info"}
			onClose={dismiss}
			action={
				state === "sent" ? undefined : (
					<Button color="inherit" size="small" onClick={() => void resend()} disabled={state === "sending"}>
						{state === "sending" ? "Sending…" : "Resend link"}
					</Button>
				)
			}
			sx={{ mb: 3, alignItems: "center" }}
		>
			{state === "sent"
				? `A new link is on its way to ${email}. It works for 24 hours.`
				: state === "failed"
					? "Could not send the link. Try again in a minute."
					: `Confirm your email: we sent a link to ${email}.`}
		</Alert>
	);
};
